import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Calendar, Clock, CreditCard, ArrowLeft } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import "../styles/BookingSuccess.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bookings/payment-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        // In a real scenario, fetch booking details here
      } else if (data.payment_status === "pending") {
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      } else {
        setPaymentStatus("failed");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("error");
    }
  };

  return (
    <div className="booking-success-page">
      <Navigation />
      
      <div className="success-container">
        {paymentStatus === "checking" && (
          <div className="status-card">
            <div className="spinner"></div>
            <h2 className="heading-2">Processing Your Payment...</h2>
            <p className="body-regular">Please wait while we confirm your booking.</p>
          </div>
        )}

        {paymentStatus === "success" && (
          <div className="status-card success">
            <CheckCircle size={80} color="#22c55e" />
            <h1 className="heading-1">Booking Confirmed!</h1>
            <p className="body-large">Thank you! Your appointment has been successfully booked.</p>
            
            <div className="confirmation-details">
              <h3 className="heading-3">What's Next?</h3>
              <div className="next-steps">
                <div className="step">
                  <CheckCircle size={24} color="var(--gold-primary)" />
                  <p>Check your email for booking confirmation</p>
                </div>
                <div className="step">
                  <Calendar size={24} color="var(--gold-primary)" />
                  <p>Add the appointment to your calendar</p>
                </div>
                <div className="step">
                  <Clock size={24} color="var(--gold-primary)" />
                  <p>Arrive 10 minutes early for your appointment</p>
                </div>
              </div>

              <div className="payment-info">
                <CreditCard size={20} />
                <p className="body-small">                  Deposit paid. Remaining balance due on arrival.
                </p>
              </div>
            </div>

            <button onClick={() => navigate("/")} className="btn-primary">
              <ArrowLeft size={20} /> Back to Home
            </button>
          </div>
        )}

        {(paymentStatus === "failed" || paymentStatus === "error" || paymentStatus === "timeout") && (
          <div className="status-card error">
            <h2 className="heading-2">Payment Issue</h2>
            <p className="body-regular">              There was an issue processing your payment. Please check your email or contact us.
            </p>
            <button onClick={() => navigate("/")} className="btn-secondary">
              Back to Home
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BookingSuccessPage;